import { AlertTriangle, Info } from 'lucide-react';
import { foundation } from '../../ui/tokens';

const { color, spacePx, radii, fontSize, fontWeight, shadow } = foundation;

// Status lives in a small tinted icon. The card itself stays white so a
// warning and an action never fight the document below for attention.
const TONES = {
  warning: { badgeBg: color.semantic.warningSoft, badgeFg: color.semantic.warning, Icon: AlertTriangle },
  danger: { badgeBg: color.semantic.dangerSoft, badgeFg: color.semantic.danger, Icon: AlertTriangle },
  info: { badgeBg: color.semantic.infoSoft, badgeFg: color.semantic.info, Icon: Info },
  action: { badgeBg: color.neutral.surfaceMuted, badgeFg: color.neutral.textSecondary, Icon: Info },
  muted: { badgeBg: null, badgeFg: color.neutral.textSecondary, Icon: null },
};

/**
 * Notice card between a preview modal's header and its PDF body (estimate +
 * invoice previews), and the same card wherever a modal needs one calm line
 * of status. White surface, hairline border, a tinted icon for the tone, and
 * an optional trailing control that looks the same on the phone and the web.
 *
 * `muted` is the quiet variant: no card, no icon — one secondary-text line.
 * Pass the message via `children` (use a `<b>…</b>` chunk + the exported
 * `noticeLead` renderer to bold the lead phrase). `icon` overrides the tone's
 * icon. `flush` drops the outer margin when the caller already pads.
 */
export default function PreviewNotice({
  tone = 'info', isMobile = false, action = null, flush = false, icon = null, children, ...rest
}) {
  const t = TONES[tone] || TONES.info;
  const Icon = icon || t.Icon;
  const quiet = tone === 'muted';
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacePx[2],
        margin: flush ? 0 : `${spacePx[3]} ${isMobile ? spacePx[3] : spacePx[5]}`,
        padding: quiet ? `0 ${spacePx[1]}` : `6px ${spacePx[2]} 6px ${spacePx[2]}`,
        borderRadius: radii.lg,
        background: quiet ? 'transparent' : color.neutral.surface,
        border: quiet ? 'none' : `1px solid ${color.neutral.border}`,
        boxShadow: quiet ? 'none' : shadow.xs,
        color: quiet ? color.neutral.textSecondary : color.neutral.text,
        fontSize: quiet ? fontSize.xs : fontSize.sm,
        lineHeight: 1.35,
        minHeight: quiet ? undefined : 40,
        boxSizing: 'border-box',
      }}
      {...rest}
    >
      {Icon && t.badgeBg && (
        <span
          aria-hidden
          style={{
            width: 26,
            height: 26,
            borderRadius: radii.md,
            background: t.badgeBg,
            color: t.badgeFg,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
      )}
      {/* `overflowWrap: anywhere` because one caller (the estimate provenance
          line) interpolates a customer-typed e-signature — free text bounded
          only at 120 chars with no charset validation, so a single unbroken
          token would otherwise run past the notice instead of wrapping. */}
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
      {action && (
        <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{action}</span>
      )}
    </div>
  );
}

/** Shared rich-text chunk: bold the lead phrase in a notice message. */
export const noticeLead = (chunks) => (
  <strong style={{ fontWeight: fontWeight.semibold }}>{chunks}</strong>
);
